import SETTINGS from '#/config/global-settings';

import { Account, AccountStatus } from '#/api/account';

import {
  DELEVERAGE_DOCS_URL,
  UNHEALTHY_DOCS_URL,
  LIQUIDATION_DOCS_URL,
} from '#/routes-external';

export interface AccountView {
  readonly status: {
    readonly label: string;
    readonly value: AccountStatus;
    readonly docsUrl: string | null;
  };
  readonly freeCollateral: string;
  readonly accountValue: string;
  readonly healthFactor: string;

  // Derived:
  readonly isBeingLiquidated: boolean;
}

const STATUS_VIEW_MAP: Record<AccountStatus, AccountView['status']> = {
  ACTIVE: {
    label: '',
    value: 'ACTIVE',
    docsUrl: null,
  },
  DELEVERAGE: {
    label: 'Deleveraging',
    value: 'DELEVERAGE',
    docsUrl: DELEVERAGE_DOCS_URL,
  },
  UNHEALTHY: {
    label: 'Unhealthy',
    value: 'UNHEALTHY',
    docsUrl: UNHEALTHY_DOCS_URL,
  },
  LIQUIDATION: {
    label: 'Liquidation',
    value: 'LIQUIDATION',
    docsUrl: LIQUIDATION_DOCS_URL,
  },
};

export const EMPTY_VIEW: AccountView = {
  status: STATUS_VIEW_MAP.ACTIVE,
  freeCollateral: '-',
  accountValue: '-',
  healthFactor: '-',
  isBeingLiquidated: false,
};

export function statusView(account: Account) {
  return STATUS_VIEW_MAP[account.status];
}

export function totalCollateralView(account: Account) {
  return `$${account.total_collateral.toFormat(SETTINGS.fiat.USD.scale)}`;
}

export function freeCollateralView(account: Account) {
  return `$${account.free_collateral.toFormat(SETTINGS.fiat.USD.scale)}`;
}

export function accountValueView(account: Account) {
  return `$${account.account_value.toFormat(SETTINGS.fiat.USD.scale)}`;
}

export function portfolioChangeView(account: Account) {
  return `$${account.portfolio_change.toFormat(SETTINGS.fiat.USD.scale)}`;
}

export function portfolioChangeDirectionView(account: Account) {
  return account.portfolio_change.isPositive() ? 'up' : 'down';
}

export function portfolioChangePercentageView(account: Account) {
  return `${account.portfolio_change_percentage.toFormat(2)}%`;
}

export function initialMarginRequirementView(account: Account) {
  return `$${account.initial_margin_requirement.toFormat(
    SETTINGS.fiat.USD.scale,
  )}`;
}

export function maintenanceMarginRequirementView(account: Account) {
  return `$${account.maintenance_margin_requirement.toFormat(
    SETTINGS.fiat.USD.scale,
  )}`;
}

export function healthFactorView(account: Account): string {
  if (account.maintenance_margin_requirement.isZero()) return '-';

  const result = account.account_value
    .dividedBy(account.maintenance_margin_requirement)
    .multipliedBy(100);
  const precision = result.isGreaterThanOrEqualTo(100) ? 0 : 2;

  return `${result.toFormat(precision)}%`;
}
